import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Row, Col, Badge, Table, Button } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../../services/api';
import PaymentModal from '../Payment/PaymentModal';
import './ClientInfo.css';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const ClientInfo = () => {
  const [dashboard, setDashboard] = useState(null);
  const [payments, setPayments] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  useEffect(() => {
    loadDashboardData();
  }, []);
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, paymentsRes, trafficRes] = await Promise.all([
        api.get('/dashboard/info'),
        api.get('/dashboard/payments'),
        api.get('/dashboard/traffic')
      ]);
      setDashboard(dashboardRes.data);
      setPayments(paymentsRes.data);
      setTraffic(trafficRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };
  if (loading) return (
    <div className="text-center p-5">
      <Spinner animation="border" style={{ width: '4rem', height: '4rem' }} />
      <p className="mt-3">Loading dashboard...</p>
    </div>
  );
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!dashboard) return <Alert variant="warning">No dashboard data</Alert>;
  const hasClientData = traffic?.client_daily && traffic.client_daily.length > 0;
  const hasSupplierData = traffic?.supplier_daily && traffic.supplier_daily.length > 0;
  const isBoth = traffic?.is_both;
  let chartData = { labels: [], datasets: [] };
  if (isBoth && hasClientData && hasSupplierData) {
    const allDates = [...new Set([
      ...traffic.client_daily.map(d => d.call_date),
      ...traffic.supplier_daily.map(d => d.call_date)
    ])].sort();
    chartData = {
      labels: allDates,
      datasets: [
        {
          label: 'Client Minutes',
          data: allDates.map(date => {
            const item = traffic.client_daily.find(d => d.call_date === date);
            return item ? parseFloat(item.duration) : 0;
          }),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Client Cost ($)',
          data: allDates.map(date => {
            const item = traffic.client_daily.find(d => d.call_date === date);
            return item ? parseFloat(item.client_cost || 0) : 0;
          }),
          backgroundColor: 'rgba(29, 78, 216, 0.8)',
          borderColor: 'rgba(29, 78, 216, 1)',
          borderWidth: 2,
          yAxisID: 'y1'
        },
        {
          label: 'Supplier Minutes',
          data: allDates.map(date => {
            const item = traffic.supplier_daily.find(d => d.call_date === date);
            return item ? parseFloat(item.duration) : 0;
          }),
          backgroundColor: 'rgba(249, 115, 22, 0.8)',
          borderColor: 'rgba(96, 165, 250, 1)',
          borderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Supplier Cost ($)',
          data: allDates.map(date => {
            const item = traffic.supplier_daily.find(d => d.call_date === date);
            return item ? parseFloat(item.vendor_cost || 0) : 0;
          }),
          backgroundColor: 'rgba(234, 88, 12, 0.8)',
          borderColor: 'rgba(147, 197, 253, 1)',
          borderWidth: 2,
          yAxisID: 'y1'
        }
      ]
    };
  } else if (hasClientData) {
    chartData = {
      labels: traffic.client_daily.map(d => d.call_date),
      datasets: [
        {
          label: 'Minutes',
          data: traffic.client_daily.map(d => parseFloat(d.duration)),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Cost ($)',
          data: traffic.client_daily.map(d => parseFloat(d.client_cost || 0)),
          backgroundColor: 'rgba(29, 78, 216, 0.8)',
          borderColor: 'rgba(29, 78, 216, 1)',
          borderWidth: 2,
          yAxisID: 'y1'
        }
      ]
    };
  } else if (hasSupplierData) {
    chartData = {
      labels: traffic.supplier_daily.map(d => d.call_date),
      datasets: [
        {
          label: 'Minutes',
          data: traffic.supplier_daily.map(d => parseFloat(d.duration)),
          backgroundColor: 'rgba(249, 115, 22, 0.8)',
          borderColor: 'rgba(96, 165, 250, 1)',
          borderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Cost ($)',
          data: traffic.supplier_daily.map(d => parseFloat(d.vendor_cost || 0)),
          backgroundColor: 'rgba(234, 88, 12, 0.8)',
          borderColor: 'rgba(147, 197, 253, 1)',
          borderWidth: 2,
          yAxisID: 'y1'
        }
      ]
    };
  }
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 14, weight: 'bold' },
          padding: 15
        }
      },
      title: {
        display: true,
        text: 'Traffic Statistics - Last 7 Days',
        font: { size: 18, weight: 'bold' },
        padding: { top: 10, bottom: 20 }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Minutes',
          font: { size: 14, weight: 'bold' }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cost ($)',
          font: { size: 14, weight: 'bold' }
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };
  return (
    <>
      <div className="p-4">
        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Card className="dashboard-card h-100">
              <div className="gradient-header company-header">
                <h4 className="mb-0">👤 {dashboard.login}</h4>
                <Button className="topup-button" onClick={() => setShowPayment(true)}>
                  💰 Top Up Balance
                </Button>
              </div>
              <Card.Body style={{ padding: '1rem' }}>
                <Row>
                  <Col md={6}>
                    <div className="balance-card">
                      <div className="balance-info">
                        <div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Current Balance</div>
                          <div className="balance-amount">
                            {parseFloat(dashboard.account_state || 0).toFixed(2)} {dashboard.currency_code || '$'}
                          </div>
                        </div>
                        <Badge bg={dashboard.active === 1 ? 'success' : 'danger'} style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
                          {dashboard.active === 1 ? '✓ Active' : '✗ Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="info-label">Client Type</div>
                      <div className="info-value">📋 {dashboard.client_type?.trim() || 'N/A'}</div>
                    </div>
                    <div className="stat-card">
                      <div className="info-label">Billing Timezone</div>
                      <div className="info-value">🌍 {dashboard.time_zone || 'N/A'}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="stat-card">
                      <div className="info-label">Account Type</div>
                      <div className="info-value">{dashboard.type === 1 ? '💳 Prepaid' : '📄 Postpaid'}</div>
                    </div>
                    <div className="stat-card">
                      <div className="info-label">Invoice Rule</div>
                      <div className="info-value">📋 {dashboard.inv_rule_id || 'N/A'}</div>
                    </div>
                    <div className="stat-card">
                      <div className="info-label">Account Manager</div>
                      <div className="info-value">👨‍💼 {dashboard.manager || 'N/A'}</div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="dashboard-card h-100">
              <div className="gradient-header">
                <h5 className="mb-0">📞 Escalation Contacts</h5>
              </div>
              <Card.Body style={{ padding: '1rem' }}>
                <Alert variant="info" className="mb-2" style={{ fontSize: '0.85rem', padding: '0.5rem' }}>
                  <strong>💡 Tip:</strong> For faster response, use Tickets menu.
                </Alert>
                <div className="contact-card">
                  <div className="contact-item">
                    <div className="contact-label">📧 Invoice Department</div>
                    <div className="contact-value">invoice@rovex-t.com</div>
                  </div>
                  <div className="contact-item">
                    <div className="contact-label">🔧 NOC (Technical Support)</div>
                    <div className="contact-value">noc@rovex-t.com</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="dashboard-card h-100">
              <div className="gradient-header">
                <h5 className="mb-0">🌐 Connection IPs</h5>
              </div>
              <Card.Body style={{ padding: '1rem' }}>
                <div className="ip-card">
                  <div className="contact-item">
                    <div className="contact-label">Voice Servers</div>
                    <div className="contact-value">
                      <strong>195.219.118.34</strong> — Port 5060 (SIP)<br/>
                      <strong>87.119.77.87</strong> — Port 5060 (SIP)<br/>
                      <strong>195.219.118.35</strong> — Port 5060 (SIP), Port 1720 (H.323)
                    </div>
                  </div>
                  <div className="contact-item">
                    <div className="contact-label">SMS Server</div>
                    <div className="contact-value">
                      <strong>195.219.118.44</strong><br/>
                      Port 2875(no SSH)/2876(SSH) (SMPP)
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="g-4 mb-4">
          <Col lg={12}>
            <Card className="dashboard-card">
              <div className="gradient-header">
                <h5 className="mb-0">📊 Traffic Overview</h5>
              </div>
              <Card.Body>
                <Row className="mb-3">
                  {hasClientData && (
                    <>
                      <Col md={isBoth ? 3 : 6}>
                        <div className="stat-card">
                          <div className="info-label">{isBoth ? 'Client Minutes (7d)' : 'Total Minutes (7d)'}</div>
                          <div className="info-value" style={{ fontSize: '1.5rem', color: '#3b82f6' }}>
                            {parseFloat(traffic?.client_summary?.duration || 0).toFixed(2)} min
                          </div>
                        </div>
                      </Col>
                      <Col md={isBoth ? 3 : 6}>
                        <div className="stat-card">
                          <div className="info-label">{isBoth ? 'Client Cost (7d)' : 'Total Cost (7d)'}</div>
                          <div className="info-value" style={{ fontSize: '1.5rem', color: '#1d4ed8' }}>
                            ${parseFloat(traffic?.client_summary?.client_cost || 0).toFixed(2)}
                          </div>
                        </div>
                      </Col>
                    </>
                  )}
                  {hasSupplierData && (
                    <>
                      <Col md={isBoth ? 3 : 6}>
                        <div className="stat-card">
                          <div className="info-label">{isBoth ? 'Supplier Minutes (7d)' : 'Total Minutes (7d)'}</div>
                          <div className="info-value" style={{ fontSize: '1.5rem', color: '#60a5fa' }}>
                            {parseFloat(traffic?.supplier_summary?.duration || 0).toFixed(2)} min
                          </div>
                        </div>
                      </Col>
                      <Col md={isBoth ? 3 : 6}>
                        <div className="stat-card">
                          <div className="info-label">{isBoth ? 'Supplier Cost (7d)' : 'Total Cost (7d)'}</div>
                          <div className="info-value" style={{ fontSize: '1.5rem', color: '#93c5fd' }}>
                            ${parseFloat(traffic?.supplier_summary?.vendor_cost || 0).toFixed(2)}
                          </div>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
                <div style={{ height: '350px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="g-4">
          <Col lg={12}>
            <Card className="dashboard-card">
              <div className="gradient-header">
                <h5 className="mb-0">💰 Recent Payments</h5>
              </div>
              <Card.Body>
                {payments.length > 0 ? (
                  <Table striped hover responsive className="payment-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Currency</th>
                        <th>Status</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, idx) => (
                        <tr key={idx}>
                          <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                          <td className={parseFloat(payment.amount) >= 0 ? 'payment-amount-positive' : 'payment-amount-negative'}>
                            {parseFloat(payment.amount) >= 0 ? '+' : ''}{parseFloat(payment.amount).toFixed(2)}
                          </td>
                          <td>{payment.currency}</td>
                          <td>
                            <Badge bg="primary">{payment.p_status_name || 'N/A'}</Badge>
                          </td>
                          <td>{payment.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted p-4">No payments yet</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <PaymentModal 
        show={showPayment} 
        onHide={() => setShowPayment(false)}
        onSuccess={() => {
          loadDashboardData();
        }}
      />
    </>
  );
};
export default ClientInfo;